'use client';

/**
 * Global receiver for files the OPERATING SYSTEM hands the app:
 *
 *   - installed PWA: `launchQueue` delivers `FileSystemFileHandle`s when the
 *     user double-clicks a registered file type (manifest `file_handlers`,
 *     action `/open`);
 *   - desktop shell: the Rust side buffers absolute paths from file
 *     associations (cold launch, macOS Apple Events, second-instance argv);
 *     drained here on mount and window focus.
 *
 * Files run through `processLocalFile` (dedup + three-way sync check) one at
 * a time; clean results navigate straight to `/w/<id>`, conflicts render the
 * modal below. Mounted once in the root layout — costs nothing until the OS
 * actually delivers a file. The `/open` route renders its status from the
 * context this exports.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { useT } from '@/components/i18n-provider';
import { X } from 'lucide-react';
import {
  processLocalFile,
  type IncomingLocalFile,
  type OpenConflict,
  type OpenErrorCode,
  type OpenOutcome,
} from '@/lib/content/open-with/open-flow';
import { isDesktopShell, statAndRead, takePendingOpens } from '@/lib/content/open-with/tauri-bridge';
import { Spinner } from '@/components/ui/spinner';

export type LaunchPhase =
  | { phase: 'idle'; sawLaunch: boolean }
  | { phase: 'working'; fileName: string }
  | { phase: 'conflict'; conflict: OpenConflict; settle: () => void }
  | { phase: 'signed-out' }
  | { phase: 'error'; code: Exclude<OpenErrorCode, 'signed-out'>; fileName?: string };

const OsFileLaunchContext = createContext<LaunchPhase>({ phase: 'idle', sawLaunch: false });

export function useOsFileLaunch(): LaunchPhase {
  return useContext(OsFileLaunchContext);
}

/** Chromium's LaunchParams surface — not yet in lib.dom. */
interface LaunchParamsLike {
  files?: ReadonlyArray<FileSystemFileHandle>;
}
interface LaunchQueueLike {
  setConsumer(consumer: (params: LaunchParamsLike) => void): void;
}

export function OsFileLaunchProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [ui, setUi] = useState<LaunchPhase>({ phase: 'idle', sawLaunch: false });
  const queueRef = useRef<IncomingLocalFile[]>([]);
  const busyRef = useRef(false);
  const userIdRef = useRef<string | null | undefined>(undefined);

  const ensureUserId = useCallback(async (): Promise<string | null> => {
    if (userIdRef.current !== undefined) return userIdRef.current;
    try {
      const res = await fetch('/api/auth/session', { credentials: 'same-origin' });
      const json = (await res.json()) as { user?: { id?: string } | null };
      userIdRef.current = json.user?.id ?? null;
    } catch {
      userIdRef.current = null;
    }
    return userIdRef.current;
  }, []);

  const handleOutcome = useCallback(
    async (outcome: OpenOutcome): Promise<void> => {
      if (outcome.kind === 'open') {
        setUi({ phase: 'idle', sawLaunch: true });
        router.push(`/w/${outcome.contentId}`);
        return;
      }
      if (outcome.kind === 'error') {
        setUi(
          outcome.code === 'signed-out'
            ? { phase: 'signed-out' }
            : { phase: 'error', code: outcome.code },
        );
        return;
      }
      // Conflict: hold the queue until the user picks a side (or dismisses).
      await new Promise<void>((settle) => {
        setUi({ phase: 'conflict', conflict: outcome.conflict, settle });
      });
    },
    [router],
  );

  const pump = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      while (queueRef.current.length) {
        const file = queueRef.current.shift()!;
        setUi({ phase: 'working', fileName: file.name });
        const userId = await ensureUserId();
        if (!userId) {
          queueRef.current = [];
          setUi({ phase: 'signed-out' });
          return;
        }
        await handleOutcome(await processLocalFile(userId, file));
      }
    } finally {
      busyRef.current = false;
    }
  }, [ensureUserId, handleOutcome]);

  const enqueue = useCallback(
    (file: IncomingLocalFile) => {
      queueRef.current.push(file);
      void pump();
    },
    [pump],
  );

  // PWA: consume OS launches. The browser buffers deliveries until a
  // consumer is set, so a launch that opened this very page is not lost.
  useEffect(() => {
    const lq = (window as unknown as { launchQueue?: LaunchQueueLike }).launchQueue;
    if (!lq) return;
    lq.setConsumer((params) => {
      void (async () => {
        for (const handle of params.files ?? []) {
          try {
            const f = await handle.getFile();
            enqueue({
              name: f.name,
              bytes: await f.arrayBuffer(),
              mimeHint: f.type || undefined,
              handle,
            });
          } catch {
            // Handle revoked between launch and read; nothing to open.
          }
        }
      })();
    });
  }, [enqueue]);

  // Desktop shell: drain buffered opens now and whenever the window regains
  // focus (an OS file-open always fronts the app, so focus is the signal).
  useEffect(() => {
    if (!isDesktopShell()) return;
    let cancelled = false;
    const drain = () => {
      void (async () => {
        for (const path of await takePendingOpens()) {
          if (cancelled) return;
          const read = await statAndRead(path);
          if (read) {
            enqueue({ name: read.name, bytes: read.bytes, originId: read.originId, path });
          }
        }
      })();
    };
    drain();
    // Focus alone is NOT a sufficient signal (verified against the packaged
    // app): an open can arrive with the app already frontmost (no focus
    // event), and even `open -a` ACTIVATES the app before the Apple Event
    // delivers the path — the focus drain races the buffer fill and loses.
    // A steady poll closes both; the IPC is a sub-ms drain of an empty Vec.
    const poll = setInterval(drain, 2000);
    window.addEventListener('focus', drain);
    document.addEventListener('visibilitychange', drain);
    return () => {
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('focus', drain);
      document.removeEventListener('visibilitychange', drain);
    };
  }, [enqueue]);

  return (
    <OsFileLaunchContext.Provider value={ui}>
      {children}
      {ui.phase === 'conflict' && (
        <ConflictDialog
          conflict={ui.conflict}
          onOutcome={(o) => {
            const settle = ui.settle;
            void handleOutcome(o).then(settle);
          }}
          onDismiss={() => {
            const settle = ui.settle;
            setUi({ phase: 'idle', sawLaunch: true });
            settle();
          }}
        />
      )}
    </OsFileLaunchContext.Provider>
  );
}

const CONFLICT_COPY: Record<
  OpenConflict['state'],
  { title: string; body: (name: string) => string }
> = {
  'cloud-newer': {
    title: 'A newer version is in your workspace',
    body: (name) => `The workspace copy of "${name}" has changes that this file does not.`,
  },
  'local-newer': {
    title: 'This file has new changes',
    body: (name) => `"${name}" changed on this computer after it was added to your workspace.`,
  },
  diverged: {
    title: 'Two versions of this file',
    body: (name) =>
      `"${name}" and its workspace copy have both changed since they were last in sync.`,
  },
};

function ConflictDialog({
  conflict,
  onOutcome,
  onDismiss,
}: {
  conflict: OpenConflict;
  onOutcome: (o: OpenOutcome) => void;
  onDismiss: () => void;
}) {
  const t = useT();
  const [resolving, setResolving] = useState(false);
  const copy = CONFLICT_COPY[conflict.state];

  const act = (run: () => Promise<OpenOutcome>) => {
    if (resolving) return;
    setResolving(true);
    void run().then(onOutcome);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button =
    'w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
    >
      <div className="relative w-full max-w-md mx-4 rounded-xl bg-card border border-border shadow-2xl p-6">
        <button
          onClick={onDismiss}
          disabled={resolving}
          className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label={t('workspace.canvas.node.close')}
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-base font-semibold tracking-tight pr-8">{copy.title}</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">{copy.body(conflict.fileName)}</p>
        <div className="mt-5 flex flex-col gap-2">
          <button
            className={`${button} bg-primary text-primary-foreground hover:bg-primary/90`}
            disabled={resolving}
            onClick={() =>
              act(() =>
                conflict.state === 'local-newer'
                  ? conflict.uploadAsCopy()
                  : conflict.openCloud(false),
              )
            }
          >
            {conflict.state === 'local-newer' ? t('osOpen.uploadNewCopy') : t('osOpen.openWorkspaceVersion')}
          </button>
          <button
            className={`${button} border border-border hover:bg-muted`}
            disabled={resolving}
            onClick={() =>
              act(() =>
                conflict.state === 'local-newer'
                  ? conflict.openCloud(false)
                  : conflict.uploadAsCopy(),
              )
            }
          >
            {conflict.state === 'local-newer' ? t('osOpen.openWorkspaceVersion') : t('osOpen.uploadNewCopy')}
          </button>
          {conflict.canUpdateLocal && (
            <button
              className={`${button} border border-border text-muted-foreground hover:bg-muted hover:text-foreground`}
              disabled={resolving}
              onClick={() => act(() => conflict.openCloud(true))}
            >
              {conflict.state === 'diverged'
                ? t('osOpen.openAndReplace')
                : t('osOpen.openAndUpdate')}
            </button>
          )}
        </div>
        {resolving && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner size="sm" />
            {t('osOpen.workingOnIt')}
          </div>
        )}
      </div>
    </div>
  );
}
